import React, { useMemo, useState, useEffect } from 'react'
import {
    Box,
    Button,
    Grid,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    TextField,
    Typography,
    InputAdornment,
} from '@material-ui/core'
import {
    CheckCircle,
    GetApp,
    MoreVert,
    Search,
    CloudUpload,
} from '@material-ui/icons'
import { ResidentTermListView } from './ResidentTermListView'
import { useResidentTerms } from '../../hooks/useResidentTerms'
import { useLeaseAuditDetail } from '../../hooks/useLeaseAuditDetail'
import { useResidentTermsRootStyles } from './styles'
import { HEADER_HEIGHT, COLLAPSABLE_SELECTOR_HEIGHT } from '../constants'

interface ResidentTermsTabProps {
    auditId: number
    isCollapsableSelectorOpen: boolean
    onOpenUpload?: () => void
}

export const ResidentTermsRoot: React.FC<ResidentTermsTabProps> = ({
    auditId,
    isCollapsableSelectorOpen,
    onOpenUpload,
}) => {
    const headerHeight = HEADER_HEIGHT
    const collapsableSelectorHeight = isCollapsableSelectorOpen
        ? COLLAPSABLE_SELECTOR_HEIGHT
        : 0
    const classes = useResidentTermsRootStyles({
        headerHeight,
        collapsableSelectorHeight,
    })
    const actionMenuSecondaryTypographyProps = {
        style: {
            whiteSpace: 'normal' as const,
            maxWidth: 260,
        },
    }
    const { leaseAudit, updateLeaseAudit } = useLeaseAuditDetail({ auditId })
    const [completionFilter, setCompletionFilter] = useState<
        'all' | 'complete' | 'reviewed' | 'needs_review'
    >('all')
    const [residentTermSearchInput, setResidentTermSearchInput] = useState('')
    const [residentTermSearchQuery, setResidentTermSearchQuery] = useState('')
    const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
        null,
    )

    const {
        residentTerms: allResidentTerms,
        isLoading: isLoadingResidentTerms,
        fetchResidentTermDocuments,
        isFetchingDocuments,
        exportMissing,
        isExportingMissing,
    } = useResidentTerms({
        auditId,
    })

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setResidentTermSearchQuery(residentTermSearchInput)
        }, 250)

        return () => {
            window.clearTimeout(timeoutId)
        }
    }, [residentTermSearchInput])

    const handleCompletionFilterChange = (
        filter: 'all' | 'complete' | 'reviewed' | 'needs_review',
    ) => {
        if (completionFilter === filter && filter !== 'all') {
            setCompletionFilter('all')
            return
        }
        setCompletionFilter(filter)
    }

    const handleActionsOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setActionsAnchorEl(event.currentTarget)
    }

    const handleActionsClose = () => {
        setActionsAnchorEl(null)
    }

    const handleExportMissing = () => {
        const termsNeedingReview = allResidentTerms.filter(
            (term) => !term.has_selected_document,
        )
        const residentTermIds = termsNeedingReview.map((term) => term.id)
        if (residentTermIds.length === 0) {
            return
        }
        exportMissing(residentTermIds)
    }

    const handleCompleteReview = () => {
        updateLeaseAudit({
            latest_ready_step: 'ATTRIBUTE_SELECTION',
        })
    }

    const filteredTerms = useMemo(() => {
        const normalizedSearch = residentTermSearchQuery.trim().toLowerCase()
        const results = allResidentTerms.filter((term) => {
            const matchesCompletion =
                completionFilter === 'all' ||
                (completionFilter === 'complete' &&
                    term.has_selected_document === true) ||
                (completionFilter === 'reviewed' &&
                    !term.has_selected_document &&
                    term.reviewed === true) ||
                (completionFilter === 'needs_review' &&
                    !term.has_selected_document &&
                    !term.reviewed)

            if (!matchesCompletion) return false
            if (!normalizedSearch) return true

            const resident = term.resident?.toLowerCase() ?? ''
            const bldgUnit = term.bldg_unit?.toLowerCase() ?? ''
            return (
                resident.includes(normalizedSearch) ||
                bldgUnit.includes(normalizedSearch)
            )
        })
        return results
    }, [completionFilter, allResidentTerms, residentTermSearchQuery])

    const residentTermStats = useMemo(
        () => ({
            total: allResidentTerms.length,
            complete: allResidentTerms.filter(
                (term) => term.has_selected_document === true,
            ).length,
            reviewed: allResidentTerms.filter(
                (term) => !term.has_selected_document && term.reviewed === true,
            ).length,
            needsReview: allResidentTerms.filter(
                (term) => !term.has_selected_document && !term.reviewed,
            ).length,
        }),
        [allResidentTerms],
    )
    return (
        <Box className={classes.container}>
            <Grid container spacing={2} className={classes.headerRow}>
                {/* search input */}
                <Grid item xs={2}>
                    <TextField
                        value={residentTermSearchInput}
                        onChange={(event) => {
                            setResidentTermSearchInput(event.target.value)
                        }}
                        placeholder="Search Resident/Bldg-Unit"
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* filter */}
                <Grid item xs={2}>
                    <Box
                        className={`${classes.statTile} ${
                            classes.statTileInteractive
                        } ${
                            completionFilter === 'all'
                                ? classes.statTileActive
                                : ''
                        }`}
                        onClick={() => handleCompletionFilterChange('all')}
                    >
                        <Box className={classes.statContent}>
                            <Typography
                                component="span"
                                className={classes.statLabel}
                            >
                                Total
                            </Typography>
                            <Typography
                                component="span"
                                className={classes.statValue}
                            >
                                {residentTermStats.total}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Box
                        className={`${classes.statTile} ${
                            classes.statTileInteractive
                        } ${
                            completionFilter === 'needs_review'
                                ? classes.statTileActive
                                : ''
                        }`}
                        onClick={() =>
                            handleCompletionFilterChange('needs_review')
                        }
                    >
                        <Box className={classes.statContent}>
                            <Typography
                                component="span"
                                className={classes.statLabel}
                            >
                                Needs Review
                            </Typography>
                            <Typography
                                component="span"
                                className={classes.statValue}
                                style={{ color: '#f59e0b' }}
                            >
                                {residentTermStats.needsReview}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Box
                        className={`${classes.statTile} ${
                            classes.statTileInteractive
                        } ${
                            completionFilter === 'reviewed'
                                ? classes.statTileActive
                                : ''
                        }`}
                        onClick={() => handleCompletionFilterChange('reviewed')}
                    >
                        <Box className={classes.statContent}>
                            <Typography
                                component="span"
                                className={classes.statLabel}
                            >
                                Reviewed
                            </Typography>
                            <Typography
                                component="span"
                                className={classes.statValue}
                                style={{ color: '#2196f3' }}
                            >
                                {residentTermStats.reviewed}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Box
                        className={`${classes.statTile} ${
                            classes.statTileInteractive
                        } ${
                            completionFilter === 'complete'
                                ? classes.statTileActive
                                : ''
                        }`}
                        onClick={() => handleCompletionFilterChange('complete')}
                    >
                        <Box className={classes.statContent}>
                            <Typography
                                component="span"
                                className={classes.statLabel}
                            >
                                Ready
                            </Typography>
                            <Typography
                                component="span"
                                className={classes.statValue}
                                style={{ color: '#10b981' }}
                            >
                                {residentTermStats.complete}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                {/* actions button */}
                <Grid item xs={2}>
                    <Box className={classes.actionButtonContainer}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<MoreVert />}
                            onClick={handleActionsOpen}
                            className={classes.actionButton}
                        >
                            Actions
                        </Button>
                        <Menu
                            anchorEl={actionsAnchorEl}
                            open={Boolean(actionsAnchorEl)}
                            onClose={handleActionsClose}
                        >
                            {/* Complete Review */}
                            <MenuItem
                                onClick={() => {
                                    handleCompleteReview()
                                    handleActionsClose()
                                }}
                            >
                                <ListItemIcon>
                                    <CheckCircle fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Complete Review"
                                    secondary='Move to next step. Befor moving on, review the resident terms in the "Needs Review" status'
                                    secondaryTypographyProps={
                                        actionMenuSecondaryTypographyProps
                                    }
                                />
                            </MenuItem>
                            {/* Export Missing */}
                            <MenuItem
                                onClick={() => {
                                    handleExportMissing()
                                    handleActionsClose()
                                }}
                                disabled={
                                    residentTermStats.needsReview === 0 ||
                                    isExportingMissing
                                }
                            >
                                <ListItemIcon>
                                    <GetApp fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        isExportingMissing
                                            ? 'Generating...'
                                            : 'Export Missing'
                                    }
                                    secondary="Export a report of all resident terms that do not have a selected document.."
                                    secondaryTypographyProps={
                                        actionMenuSecondaryTypographyProps
                                    }
                                />
                            </MenuItem>
                            {/* Upload Documents */}
                            <MenuItem
                                onClick={() => {
                                    onOpenUpload?.()
                                    handleActionsClose()
                                }}
                                disabled={leaseAudit?.audit_type !== 'UPLOADED'}
                            >
                                <ListItemIcon>
                                    <CloudUpload fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Upload Documents"
                                    secondary="Upload more documents. Parsing and selection will rerun; previously selected documents may change."
                                    secondaryTypographyProps={
                                        actionMenuSecondaryTypographyProps
                                    }
                                />
                            </MenuItem>
                        </Menu>
                    </Box>
                </Grid>
            </Grid>

            <ResidentTermListView
                auditId={auditId}
                allResidentTerms={allResidentTerms}
                filteredTerms={filteredTerms}
                isLoadingResidentTerms={isLoadingResidentTerms}
                isFetchingDocuments={isFetchingDocuments}
                fetchResidentTermDocuments={fetchResidentTermDocuments}
                completionFilter={completionFilter}
                residentSearchQuery={residentTermSearchQuery}
            />
        </Box>
    )
}
